<!-- ダッシュボード セレクション選択時のページ -->
<script setup lang="ts">
  import { ref } from "vue";
  import { useLibraryStore } from "~/stores/LibraryStore";
  import { Library } from "~/entities/Library";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  const libraryStore = useLibraryStore();

  const isTablet = isTabletAndSpDisplayRef();

  interface Props {
    planInfo: PlanInfo;
    selectedLibrary: Library;
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  interface Emits {
    (event: "onBack"): void;
  }
  defineEmits<Emits>();

  const contents = ref<any[]>([]);
  const emptyTextContent = "コンテンツが提供されていません。";

  // データ取得中のフラグ。通信のみ
  // データ取得ローディング表示フラグ。スクロール判定中も表示する
  const loading = ref(false);
  const page = ref(1);
  const pageSize = ref(20);
  const totalPages = ref(0);

  const contentAreaRef = ref<HTMLElement | null>(null);
  const { height: contentAreaElementHeight } = useElementBounding(contentAreaRef);

  // データ取得
  const getLibraryContents = async (wait = false) => {
    if (loading.value) return;
    loading.value = true;
    if (wait) {
      // Safari の場合、ウェイトを入れないとスクロールがガタガタになるので調整する (Chrome, Edge は問題なし)
      if (isSafari()) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    try {
      const { contents: libraryContents, totalPages: totalPage } = await libraryStore.getLibrary(
        props.selectedLibrary.libraryId,
        page.value,
        pageSize.value,
      );
      await libraryStore.getContentsProgress(libraryContents);
      libraryStore.getDirectoryProgress(libraryContents);
      contents.value = libraryStore.mergeContentsList(contents.value, libraryContents, true);
      totalPages.value = totalPage;
      page.value++;
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => {
      return props.selectedLibrary.libraryId;
    },
    (newValue, oldValue) => {
      if (oldValue !== undefined) {
        page.value = 1;
        totalPages.value = 0;
        contents.value = [];
        getLibraryContents();
      }
    },
    { immediate: true },
  );

  const load = async ({ done }: any) => {
    if (totalPages.value > 0 && page.value > totalPages.value) {
      done("empty");
      return;
    }
    const { contents: libraryContents, totalPages: totalPage } = await libraryStore.getLibrary(
      props.selectedLibrary.libraryId,
      page.value,
      pageSize.value,
    );
    await libraryStore.getContentsProgress(libraryContents);
    contents.value = libraryStore.mergeContentsList(contents.value, libraryContents, true);
    libraryStore.getDirectoryProgress(contents.value);
    totalPages.value = totalPage;

    if (page.value <= totalPages.value) {
      page.value++;
      done("ok");
    } else {
      done("empty");
    }
  };

  const clickEditLibrary = () => {
    historyStore.to(`/plan/${props.planInfo.id}/library/edit`);
  };

  const clickLibraryContent = (content: any) => {
    historyStore.to(`/plan/${props.planInfo.id}/selection/${props.selectedLibrary.libraryId}?contentId=${content.id}`);
  };
</script>

<template>
  <MoleculesContainerMain
    v-if="selectedLibrary && selectedLibrary.libraryId"
    :title="isTablet ? selectedLibrary.libraryTitle : ''"
    :description="isTablet ? selectedLibrary.librarySummary : ''"
    :is-library="true"
    :is-complete="selectedLibrary.isComplete"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <AtomsSvgIcon
        v-if="isTablet && planInfo!.isOwner"
        class="cursor-pointer"
        :width="20"
        :height="20"
        img-path="assets/images/icon/gear.svg"
        @click="clickEditLibrary"
      />
    </template>
    <template #header-extra>
      <div v-if="!isTablet" class="d-flex align-center">
        <div class="flex-grow-1">
          <div class="library_title d-flex align-center">
            <span>{{ selectedLibrary.libraryTitle }}</span>
            <span v-if="selectedLibrary.isComplete" class="library_complete">Complete!</span>
          </div>
          <div class="library_summary">{{ selectedLibrary.librarySummary }}</div>
        </div>
        <AtomsSvgIcon
          v-if="!isTablet && planInfo!.isOwner"
          class="cursor-pointer"
          :width="20"
          :height="20"
          img-path="assets/images/icon/gear.svg"
          @click="clickEditLibrary"
        />
      </div>
    </template>
    <!--セレクションコンテンツ-->
    <div ref="contentAreaRef" class="h-100">
      <div v-if="!loading && contents.length == 0" class="mt-8 mx-3 mx-sm-6" style="margin-bottom: 200px">
        {{ emptyTextContent }}
      </div>
      <!-- contents -->
      <v-infinite-scroll :height="contentAreaElementHeight" class="infinitie-scroll" @load="load">
        <MoleculesLibraryList
          :contents="contents"
          :selected-library="selectedLibrary"
          @click-library-content="clickLibraryContent"
        />
        <template #empty>
          <div class=""></div>
        </template>
      </v-infinite-scroll>
    </div>
  </MoleculesContainerMain>
</template>
<style lang="scss">
  @import "assets/media.scss";
  .library_title {
    vertical-align: middle;
    font-size: 20px;
    font-weight: 700;
    color: white;
    @include display_sp {
      margin-top: 16px;
    }
  }
  .library_complete {
    font-size: 13px;
    padding: 2px 3px 4px 3px;
    background-color: rgb(var(--v-theme-primary));
    border-radius: 3px;
    margin-left: 4px;
    @include display_sp {
      font-size: 10px;
    }
  }
  .library_summary {
    font-size: 14px;
    font-weight: 400;
    color: #aaaaaa;
    vertical-align: bottom;
  }

  .infinitie-scroll {
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
